// SlackCallback — landing page for the Slack OAuth redirect.
//
// Flow:
//   1. Slack redirects the user to `/slack/callback?code=…&state=…`.
//   2. We POST {code, state} to /api/slack/oauth/callback to finalise the
//      bind on the server.
//   3. If we are running inside a popup (i.e. `window.opener` is the SPA),
//      post a `slack:bound` message back to the opener and close ourselves.
//   4. Otherwise — the user came here via a same-tab fallback because the
//      popup was blocked — redirect to `/settings?slack=connected`.
//
// Error path mirrors the success path: a `slack:bind-error` message in the
// popup case, `?slack=error` in the same-tab case.
//
// CONTRACT: this page never reads or writes a Slack token. The
// `postMessage` payload contains only public identifiers (employeeId,
// slackUserId), never tokens.
//
// Per ADR-001 the page renders a Tailwind v4 + lucide-react surface — no
// Cloudscape components. Visual: full-page centered card on the Forest
// surface, 480px max-width, Tiempos serif heading, lucide Loader2 spinner.

import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertCircle, Check, Loader2 } from 'lucide-react';

import { completeSlackOauth, popupOrigin } from '@/api/slack';
import { usePageEnter } from '@/hooks/useAppleMotion';

type Phase = 'pending' | 'success' | 'error';

interface SlackCallbackProps {
  /** Optional override of the SPA origin — only used by tests. */
  readonly expectedOrigin?: string;
}

export default function SlackCallback({
  expectedOrigin,
}: SlackCallbackProps): React.ReactElement {
  const [params] = useSearchParams();
  const [phase, setPhase] = useState<Phase>('pending');
  const [errorReason, setErrorReason] = useState<string | null>(null);
  const { className: pageEnterClass } = usePageEnter();
  // Guard against double-effects in React StrictMode dev — we only want to
  // POST the callback once per mount.
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const code = params.get('code');
    const state = params.get('state');
    const oauthError = params.get('error');

    if (oauthError) {
      handleError(oauthError, expectedOrigin);
      setErrorReason(oauthError);
      setPhase('error');
      return;
    }

    if (!code || !state) {
      handleError('missing_params', expectedOrigin);
      setErrorReason('missing_params');
      setPhase('error');
      return;
    }

    let cancelled = false;
    void (async () => {
      try {
        const binding = await completeSlackOauth(code, state);
        if (cancelled) return;
        handleSuccess(binding, expectedOrigin);
        setPhase('success');
      } catch {
        if (cancelled) return;
        handleError('callback_failed', expectedOrigin);
        setErrorReason('callback_failed');
        setPhase('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [params, expectedOrigin]);

  return (
    <div className={`min-h-screen bg-wayne-bg text-wayne-ink flex items-center justify-center px-6 py-12 ${pageEnterClass}`}>
      <div className="w-full max-w-[480px] flex flex-col items-center text-center">
        <h2
          className="font-serif text-wayne-ink mb-4"
          style={{
            fontSize: 26,
            fontWeight: 500,
            letterSpacing: '-0.015em',
            lineHeight: 1.2,
          }}
        >
          {phase === 'success'
            ? 'Slack connected'
            : phase === 'error'
              ? 'Could not finish the Slack bind'
              : 'Connecting Slack…'}
        </h2>

        {phase === 'pending' ? (
          <div
            className="flex items-center gap-2 text-[14px] text-wayne-ink-2"
            role="status"
            aria-live="polite"
          >
            <Loader2
              size={16}
              className="animate-spin text-wayne-ink-3"
              aria-hidden="true"
            />
            <span>Finalising the bind. This window will close shortly.</span>
          </div>
        ) : null}

        {phase === 'success' ? (
          <div
            role="status"
            className="w-full border border-wayne-hairline bg-wayne-surface rounded-lg p-4 flex items-start gap-2 text-[13px] border-l-4 border-l-wayne-green text-left"
          >
            <Check
              size={14}
              className="text-wayne-green mt-0.5 shrink-0"
              aria-hidden="true"
            />
            <div className="text-wayne-ink-2">
              You can close this window — we updated the Wayne app for you.
            </div>
          </div>
        ) : null}

        {phase === 'error' ? (
          <div
            role="alert"
            className="w-full border border-wayne-hairline bg-wayne-surface rounded-lg p-4 flex items-start gap-2 text-[13px] border-l-4 border-l-wayne-red text-left"
          >
            <AlertCircle
              size={14}
              className="text-wayne-red mt-0.5 shrink-0"
              aria-hidden="true"
            />
            <div className="text-wayne-ink-2">
              The Slack callback failed
              {errorReason ? ` (${errorReason})` : ''}. Close this window and
              try again from the Wayne app.
            </div>
          </div>
        ) : null}

        {phase !== 'pending' ? (
          <div className="mt-6 flex items-center gap-3">
            <a
              href="/"
              className="bg-wayne-accent hover:bg-wayne-accent-2 text-white rounded-full px-5 h-10 text-[13px] font-medium transition-colors inline-flex items-center no-underline"
            >
              Open Wayne
            </a>
            {phase === 'error' ? (
              <a
                href="/onboarding"
                className="bg-wayne-surface hover:bg-wayne-surface-2 border border-wayne-hairline text-wayne-ink rounded-full px-5 h-10 text-[13px] font-medium transition-colors inline-flex items-center no-underline"
              >
                Try again
              </a>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

interface BindingLike {
  readonly slackUserId?: string;
  readonly slackTeamId?: string;
  readonly slackUserDisplayName?: string;
  readonly employeeId?: string;
}

function isPopup(): boolean {
  if (typeof window === 'undefined') return false;
  // `window.opener` is a no-op in same-tab navigations. When a popup was
  // blocked the user landed here via `window.location.assign`, in which
  // case `opener` is null and we redirect instead.
  return Boolean(window.opener) && window.opener !== window;
}

function handleSuccess(binding: BindingLike, expectedOrigin?: string): void {
  if (typeof window === 'undefined') return;
  const origin = expectedOrigin ?? popupOrigin();
  if (isPopup() && origin) {
    window.opener?.postMessage(
      {
        type: 'slack:bound',
        employeeId: binding.employeeId ?? '',
        slackUserId: binding.slackUserId ?? '',
        slackTeamId: binding.slackTeamId,
        slackUserDisplayName: binding.slackUserDisplayName,
      },
      origin,
    );
    window.close();
    return;
  }
  window.location.assign('/settings?slack=connected');
}

function handleError(reason: string, expectedOrigin?: string): void {
  if (typeof window === 'undefined') return;
  const origin = expectedOrigin ?? popupOrigin();
  if (isPopup() && origin) {
    window.opener?.postMessage(
      { type: 'slack:bind-error', error: reason },
      origin,
    );
    window.close();
    return;
  }
  window.location.assign(`/settings?slack=error&reason=${encodeURIComponent(reason)}`);
}
